import keycloak from "./keycloakConfig";
import { getSecureItem, setSecureItem } from "../storage/secureStorage";
import { captureException, addBreadcrumb } from "../sentry/sentryConfig";

export interface KeycloakAuthState {
  isAuthenticated: boolean;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
}

const REFRESH_TOKEN_KEY = "keycloak_refresh_token";
const TOKEN_KEY = "keycloak_token";

class KeycloakService {
  private static instance: KeycloakService;
  private authState: KeycloakAuthState = {
    isAuthenticated: false,
    token: null,
    refreshToken: null,
    isLoading: true,
  };
  private listeners: Array<(state: KeycloakAuthState) => void> = [];
  private initializationPromise: Promise<boolean> | null = null;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): KeycloakService {
    if (!KeycloakService.instance) {
      KeycloakService.instance = new KeycloakService();
    }
    return KeycloakService.instance;
  }

  async initialize(): Promise<boolean> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    if (this.isInitialized) {
      return this.authState.isAuthenticated;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<boolean> {
    try {
      // Try to restore tokens from storage
      const storedToken = await getSecureItem(TOKEN_KEY);
      const storedRefreshToken = await getSecureItem(REFRESH_TOKEN_KEY);

      const authenticated = await keycloak.init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri: window.location.origin + "/silent-check-sso.html",
        checkLoginIframe: true,
        checkLoginIframeInterval: 5,
        pkceMethod: "S256",
        flow: "standard",
        enableLogging: false,
        token: storedToken || undefined,
        refreshToken: storedRefreshToken || undefined,
      });

      if (authenticated) {
        this.setupTokenRefresh();
        await this.saveTokens();
      } else {
        await this.clearStoredTokens();
      }

      this.updateAuthState({
        isAuthenticated: authenticated,
        token: keycloak.token || null,
        refreshToken: keycloak.refreshToken || null,
        isLoading: false,
      });

      if (authenticated) {
        addBreadcrumb("Keycloak initialized successfully", "auth", "info");
      } else {
        addBreadcrumb("Keycloak initialized - user not authenticated", "auth", "info");
      }

      this.isInitialized = true;
      return authenticated;
    } catch (error) {
      console.error("Failed to initialize Keycloak:", error);
      addBreadcrumb("Keycloak initialization failed", "auth", "error", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "keycloak_initialization_failed" },
        level: "error",
      });
      this.updateAuthState({
        isAuthenticated: false,
        token: null,
        refreshToken: null,
        isLoading: false,
      });
      this.isInitialized = true;
      return false;
    } finally {
      this.initializationPromise = null;
    }
  }

  private setupTokenRefresh() {
    keycloak.onTokenExpired = () => {
      addBreadcrumb("Token expired - refreshing", "auth", "warning");
      this.refreshToken();
    };

    setInterval(() => {
      if (keycloak.token) {
        keycloak
          .updateToken(300)
          .then((refreshed) => {
            if (refreshed) {
              addBreadcrumb("Token refreshed proactively", "auth", "info");
              this.updateAuthState({
                ...this.authState,
                token: keycloak.token || null,
                refreshToken: keycloak.refreshToken || null,
              });
            }
          })
          .catch((error) => {
            console.warn("Failed to refresh token");
            addBreadcrumb("Proactive token refresh failed", "auth", "warning", {
              error: error instanceof Error ? error.message : String(error),
            });
            captureException(error, {
              tags: { error_type: "proactive_token_refresh_failed" },
              level: "warning",
            });
          });
      }
    }, 60000);
  }

  async login(): Promise<void> {
    try {
      addBreadcrumb("Attempting Keycloak login", "auth", "info");
      await keycloak.login({
        redirectUri: window.location.href,
      });
      addBreadcrumb("Keycloak login successful", "auth", "info");
    } catch (error) {
      console.error("Login failed:", error);
      addBreadcrumb("Keycloak login failed", "auth", "error", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "keycloak_login_failed" },
        level: "error",
      });
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      addBreadcrumb("Attempting Keycloak logout", "auth", "info");
      await this.clearStoredTokens();
      localStorage.clear();

      await keycloak.logout({
        redirectUri: window.location.href,
        logoutMethod: "POST",
      });
      addBreadcrumb("Keycloak logout successful", "auth", "info");
    } catch (error) {
      console.error("Logout failed:", error);
      addBreadcrumb("Keycloak logout failed", "auth", "error", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "keycloak_logout_failed" },
        level: "error",
      });
      throw error;
    }
  }

  async refreshToken(): Promise<string | null> {
    try {
      addBreadcrumb("Attempting token refresh", "auth", "info");
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        addBreadcrumb("Token refresh successful", "auth", "info");
        this.updateAuthState({
          ...this.authState,
          token: keycloak.token || null,
          refreshToken: keycloak.refreshToken || null,
        });
        return keycloak.token || null;
      }
      addBreadcrumb("Token refresh - token still valid", "auth", "info");
      return this.authState.token;
    } catch (error) {
      console.error("Token refresh failed:", error);
      addBreadcrumb("Token refresh failed - logging out", "auth", "error", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "token_refresh_failed_logout" },
        extra: {
          error_message: error instanceof Error ? error.message : String(error),
        },
        level: "error",
      });
      await this.logout();
      return null;
    }
  }

  getAuthState(): KeycloakAuthState {
    return this.authState;
  }

  getToken(): string | null {
    return keycloak.token || null;
  }

  getRefreshToken(): string | null {
    return keycloak.refreshToken || null;
  }

  isAuthenticated(): boolean {
    return this.authState.isAuthenticated;
  }

  subscribe(listener: (state: KeycloakAuthState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private updateAuthState(newState: KeycloakAuthState) {
    this.authState = newState;
    this.listeners.forEach((listener) => listener(this.authState));

    if (newState.isAuthenticated && newState.token && newState.refreshToken) {
      this.saveTokens().catch((error) => {
        console.warn("Failed to save tokens to storage:", error);
      });
    }
  }

  private async saveTokens(): Promise<void> {
    try {
      if (keycloak.token && keycloak.refreshToken) {
        await setSecureItem(TOKEN_KEY, keycloak.token);
        await setSecureItem(REFRESH_TOKEN_KEY, keycloak.refreshToken);
        addBreadcrumb("Tokens saved to secure storage", "auth", "info");
      }
    } catch (error) {
      console.error("Failed to save tokens:", error);
      addBreadcrumb("Failed to save tokens to secure storage", "auth", "error", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "token_storage_failed" },
        level: "warning",
      });
    }
  }

  private async clearStoredTokens(): Promise<void> {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      addBreadcrumb("Stored tokens cleared", "auth", "info");
    } catch (error) {
      console.error("Failed to clear stored tokens:", error);
      addBreadcrumb("Failed to clear stored tokens", "auth", "warning", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "token_clear_failed" },
        level: "warning",
      });
    }
  }
}

export default KeycloakService;
